import PaginatedTable from '../../components/ui/PaginatedTable';
import PermissionButton from "../../components/ui/PermissionButton";
import TableActions from '../../components/ui/TableActions';
import { useEffect, useState } from "react";
import { X, Plus, Pencil, Trash2 } from "lucide-react";

export default function ExpenseCategoryModal({ onClose }) {
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const [editing, setEditing] = useState(null);

  const [form, setForm] = useState({
    name: "",
    account_id: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    const [catRes, accRes] = await Promise.all([
      fetch("/api/expensescategories", {
        credentials: "include",
      }),
      fetch("/api/accounts", {
        credentials: "include",
      }),
    ]);

    const cat = await catRes.json();
    const acc = await accRes.json();

    if (cat.status === "success") {
      setCategories(cat.data);
    }

    if (acc.status === "success") {
      setAccounts(acc.data);
    }
  }

  async function save() {
    if (!form.name || !form.account_id) {
      return alert("Fill all fields");
    }

    const url = editing
      ? `/api/expensescategories/${editing.id}`
      : "/api/expensescategories";

    const method = editing ? "PUT" : "POST";

    const res = await fetch(url, {
      method,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(form),
    });

    const data = await res.json();

    if (data.status === "success") {
      setEditing(null);

      setForm({
        name: "",
        account_id: "",
      });

      loadData();
    } else {
      alert(data.message);
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete category?")) return;

    await fetch(`/api/expensescategories/${id}`, {
      method: "DELETE",
      credentials: "include",
    });

    loadData();
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex justify-center items-center z-50">
      <div className="w-full max-w-3xl rounded-2xl bg-[#09090B] border border-white/10">
        <div className="flex justify-between items-center px-6 py-5 border-b border-white/10">
          <h2 className="text-2xl font-serif">Expense Categories</h2>

          <PermissionButton permissionModule="expense_categories" onClick={onClose}>
            <X />
          </PermissionButton>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <input
              placeholder="Category Name"
              value={form.name}
              onChange={(e) =>
                setForm({
                  ...form,
                  name: e.target.value,
                })
              }
              className="rounded-xl bg-black/40 border border-white/10 px-4 py-3"
            />

            <select
              value={form.account_id}
              onChange={(e) =>
                setForm({
                  ...form,
                  account_id: e.target.value,
                })
              }
              className="rounded-xl bg-black/40 border border-white/10 px-4 py-3"
            >
              <option value="">Select Expense Account</option>

              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} - {a.name}
                </option>
              ))}
            </select>
          </div>

          <PermissionButton permissionModule="expense_categories"
            onClick={save}
            className="px-5 py-2 rounded-xl bg-amber-400 text-black flex items-center gap-2"
          >
            <Plus size={16} />

            {editing ? "Update" : "Add Category"}
          </PermissionButton>

          <PaginatedTable className="w-full">
            <thead>
              <tr className="text-gray-400">
                <th className="text-left py-3">Category</th>

                <th className="text-left">Account</th>

                <th></th>
              </tr>
            </thead>

            <tbody>
              {categories.map((c) => (
                <tr key={c.id} className="border-t border-white/10">
                  <td className="py-3">{c.name}</td>

                  <td>{c.account}</td>

                  <td className="text-right"><TableActions>
                    <PermissionButton permissionModule="expense_categories" permissionAction="edit"
                      onClick={() => {
                        setEditing(c);

                        setForm({
                          name: c.name,
                          account_id: c.account_id,
                        });
                      }}
                      className="mr-2"
                    >
                      <Pencil size={16} />
                    </PermissionButton>

                    <PermissionButton permissionModule="expense_categories" onClick={() => remove(c.id)}>
                      <Trash2 size={16} className="text-red-400" />
                    </PermissionButton>
                  </TableActions></td>
                </tr>
              ))}
            </tbody>
          </PaginatedTable>
        </div>
      </div>
    </div>
  );
}
